<script setup>
import { computed } from 'vue';
import {
  Button,
  Divider,
  FilterPanel,
  LimitedSelectList,
  SelectButton,
} from '@dashhudson/dashing-ui';
import { LIBRARY_MEDIA_FILTER_TOOLTIPS } from '@/app/library/constants';
import { CHILI_PIPER_FORMS_MAP } from '@/app/settings/constants';
import { useRouter } from 'vue-router';
import { useIdentityStore } from '@/stores/identity';
import { useContentTagsStore } from '@/stores/content-tags';
import { useChiliPiperStore } from '@/stores/chili-piper';
import { useTrackingStore } from '@/stores/tracking';
import { BRAND, ORGANIZATION_USER } from '@/models/auth/permissions.enum';

const props = defineProps({
  showMatchTypeSelector: { type: Boolean, default: true },
  noRemember: { type: Boolean, default: false },
  modelValue: { type: Array, default: () => [] },
  matchType: { type: String, default: 'ANY' },
});

const emit = defineEmits(['update:modelValue', 'update:matchType']);

const router = useRouter();
const identityStore = useIdentityStore();
const contentTagsStore = useContentTagsStore();
const trackingStore = useTrackingStore();
const chiliPiperStore = useChiliPiperStore();

const tags = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
});

const selectedMatchType = computed({
  get: () => props.matchType,
  set: (value) => emit('update:matchType', value),
});

const contentTagOptions = computed(() =>
  contentTagsStore.currentBrandContentTags.map((tag) => ({ label: tag.name, value: tag.name })),
);

const hasContentTagsAccess = computed(() =>
  identityStore.guard(BRAND.TAGS.CAN_ACCESS_CONTENT_TAGS),
);

const canManageContentTags = computed(
  () =>
    identityStore.guard(ORGANIZATION_USER.TAGS.CAN_MANAGE_CONTENT_TAGS) ||
    identityStore.isCurrentBrandRoleAdmin,
);

const manageContentTagsTooltip = computed(() =>
  !canManageContentTags.value
    ? 'Contact your brand admin for permission to manage Content Tags'
    : null,
);

const hideRememberToggle = computed(
  () =>
    !hasContentTagsAccess.value ||
    (hasContentTagsAccess.value && !contentTagOptions.value.length) ||
    props.noRemember,
);

function redirectToSettingsContentTags() {
  router.push({
    name: 'settings.contentTags',
    params: { brandLabel: identityStore.currentBrandLabel },
  });
  trackingStore.track('Manage Tags', {
    page: 'Settings - ContentTags',
    previousPage: router.currentRoute.value.name,
  });
}

function tryContentTagsButtonClicked() {
  const formRouter = CHILI_PIPER_FORMS_MAP.enterprise.upgrade.router;
  const formName = CHILI_PIPER_FORMS_MAP.enterprise.upgrade.name;
  chiliPiperStore.submit(formRouter, formName, 'contact us');
}
</script>

<template>
  <FilterPanel
    title="Content Tags"
    :filters="showMatchTypeSelector ? ['contentTags', 'contentTagMatchType'] : 'contentTags'"
    :tooltip="LIBRARY_MEDIA_FILTER_TOOLTIPS.CONTENT_TAGS"
    :no-remember="hideRememberToggle"
  >
    <template v-if="hasContentTagsAccess && !contentTagOptions.length">
      <p class="dash-typo-base dash-text-subtle !mb-3 !mt-2">
        Use Content Tags to organize your content into custom groups for faster reporting
      </p>
      <div class="!grid !place-items-center">
        <Button
          data-test="content-tags-empty-state-button"
          severity="tertiary"
          :disabled="!canManageContentTags"
          @click="redirectToSettingsContentTags"
          >Add Content Tags</Button
        >
      </div>
    </template>
    <template v-else-if="!hasContentTagsAccess">
      <p class="dash-typo-base dash-text-subtle !mb-3 !mt-2">
        Get started with Content Tags by exploring the Enterprise plan
      </p>
      <div class="!grid !place-items-center">
        <Button severity="tertiary" @click="tryContentTagsButtonClicked">Try Content Tags</Button>
      </div>
    </template>
    <template v-else>
      <SelectButton
        v-if="showMatchTypeSelector"
        v-model="selectedMatchType"
        class="!mb-2"
        :options="[
          { value: 'ANY', label: 'Match Any' },
          { value: 'ALL', label: 'Match All' },
        ]"
        :allow-empty="false"
        full-width
        variant="dark"
      />
      <LimitedSelectList
        v-model="tags"
        data-test="content-tag-list"
        :options="contentTagOptions"
        :search-placeholder="'Search Content Tags'"
        multiple
        searchable
      >
        <template #header>
          <Button
            v-dash-tooltip="manageContentTagsTooltip"
            text
            severity="primary"
            class="dash-my-3"
            :disabled="!canManageContentTags"
            @click="redirectToSettingsContentTags"
            >Manage Content Tags</Button
          >
          <Divider class="!mb-0" />
        </template>
      </LimitedSelectList>
    </template>
  </FilterPanel>
</template>
